export type PlatformMode = 'hosted-web' | 'ios-hybrid' | 'android-hybrid' | 'dev';

export interface PlatformCapabilities {
  readonly mode: PlatformMode;
  readonly canRunSharedReactApp: boolean;
  readonly canUseHybridWrapper: boolean;
  readonly canPersistLocalDeviceMedia: boolean;
  readonly canCreateUploadBackedProjects: boolean;
  readonly uploadBackedProjectsAreQuotaLimited: boolean;
  readonly canExposeSharing: boolean;
  readonly canShowPaymentUpgradeUi: boolean;
  readonly canAccessDevOnlyRoutes: boolean;
}

export interface PlatformEnvironment {
  readonly nodeEnv?: string;
  readonly platformTarget?: string;
  readonly enableQuarantinedProductSurfaces?: string;
  readonly enableSharing?: string;
  readonly enablePayments?: string;
}

const MOBILE_PLATFORM_MODES: ReadonlySet<PlatformMode> = new Set<PlatformMode>([
  'ios-hybrid',
  'android-hybrid'
]);

export const readPlatformEnvironment = (
  env: NodeJS.ProcessEnv = process.env
): PlatformEnvironment => ({
  nodeEnv: env.NODE_ENV,
  platformTarget: env.REACT_APP_PLATFORM_TARGET,
  enableQuarantinedProductSurfaces: env.REACT_APP_ENABLE_QUARANTINED_PRODUCT_SURFACES,
  enableSharing: env.REACT_APP_ENABLE_SHARING,
  enablePayments: env.REACT_APP_ENABLE_PAYMENTS
});

export const resolvePlatformMode = ({
  platformTarget
}: Pick<PlatformEnvironment, 'platformTarget'>): PlatformMode => {
  switch (platformTarget) {
    case 'ios-hybrid':
    case 'android-hybrid':
    case 'dev':
    case 'hosted-web':
      return platformTarget;
    default:
      return 'hosted-web';
  }
};

const isMobileMode = (mode: PlatformMode): boolean => MOBILE_PLATFORM_MODES.has(mode);

const canExposeQuarantinedSurface = (
  mode: PlatformMode,
  env: PlatformEnvironment,
  legacyToggle?: string
): boolean =>
  env.nodeEnv !== 'production' &&
  !isMobileMode(mode) &&
  env.enableQuarantinedProductSurfaces === 'true' &&
  legacyToggle === 'true';

export const resolvePlatformCapabilities = (
  env: PlatformEnvironment = readPlatformEnvironment()
): PlatformCapabilities => {
  const mode = resolvePlatformMode(env);
  const mobileMode = isMobileMode(mode);

  return {
    mode,
    canRunSharedReactApp: true,
    canUseHybridWrapper: mobileMode,
    canPersistLocalDeviceMedia: mobileMode,
    canCreateUploadBackedProjects: true,
    uploadBackedProjectsAreQuotaLimited: true,
    canExposeSharing: canExposeQuarantinedSurface(mode, env, env.enableSharing),
    canShowPaymentUpgradeUi: canExposeQuarantinedSurface(mode, env, env.enablePayments),
    canAccessDevOnlyRoutes: mode === 'dev' && env.nodeEnv !== 'production'
  };
};

export const getCurrentPlatformCapabilities = (): PlatformCapabilities =>
  resolvePlatformCapabilities(readPlatformEnvironment());
